import React, { useEffect, useState } from 'react';
import { brandingAPI, publicMemberSponsorshipSubmit, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CheckCircle, Loader2, Heart } from 'lucide-react';
import { toast, Toaster } from 'sonner';

export default function PublicMemberSponsorship() {
  const [branding, setBranding] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [amountChoice, setAmountChoice] = useState('preset'); // 'preset' | 'other'
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    amount: '',
    payment_status: 'yet_to_pay',
    notes: '',
  });

  const presetAmount = Number(branding?.member_sponsorship_default_amount) || 365;

  useEffect(() => {
    brandingAPI.getPublic().then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const orgName = branding?.organization_name || 'Illawarra Indian Doctors Association';
  const logoSrc = branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }
    const finalAmount = amountChoice === 'preset' ? presetAmount : parseFloat(form.amount);
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) {
      toast.error('Please enter a contribution amount greater than zero');
      return;
    }
    setSubmitting(true);
    try {
      await publicMemberSponsorshipSubmit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        amount: finalAmount,
        payment_status: form.payment_status,
        notes: form.notes.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <img src={logoSrc} alt={orgName} className="h-20 w-auto object-contain mx-auto mb-4" />
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2" data-testid="member-submit-success-title">Thank you!</h2>
            <p className="text-muted-foreground mb-2">
              Your member contribution has been recorded.
            </p>
            <p className="text-sm text-muted-foreground">
              An admin will review your submission and follow up with a tax invoice by email.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-center" />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-3">
          <img src={logoSrc} alt={orgName} className="h-16 w-auto object-contain" />
          <div>
            <h1 className="font-semibold text-foreground">{orgName}</h1>
            <p className="text-xs text-muted-foreground">Member Sponsorship</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Member Sponsorship
            </CardTitle>
            <CardDescription>
              Thank you for supporting the {orgName}. Your sponsorship helps fund our independent
              continuing professional development (CPD) programme. Sponsorships are tax-deductible.
            </CardDescription>
          </CardHeader>
        </Card>

        {(branding?.bank_account_number || branding?.bank_bsb) && (
          <Card className="border-border" data-testid="public-bank-details">
            <CardHeader>
              <CardTitle className="text-base">Bank details</CardTitle>
              <CardDescription>If paying by direct deposit, please use these details.</CardDescription>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              {branding?.bank_account_name && (
                <p><span className="font-medium text-foreground">Account name:</span> {branding.bank_account_name}</p>
              )}
              {branding?.bank_bsb && (
                <p><span className="font-medium text-foreground">BSB:</span> {branding.bank_bsb}</p>
              )}
              {branding?.bank_account_number && (
                <p><span className="font-medium text-foreground">Account number:</span> {branding.bank_account_number}</p>
              )}
              {branding?.abn && (
                <p><span className="font-medium text-foreground">ABN:</span> {branding.abn}</p>
              )}
              <p className="text-xs text-muted-foreground pt-2">
                Use your name as the payment reference, then complete the form below so we can match it up.
              </p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Your details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ms-name">Full name *</Label>
                <Input
                  id="ms-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dr Jane Smith"
                  data-testid="member-form-name"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ms-email">Email *</Label>
                  <Input
                    id="ms-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@example.com"
                    data-testid="member-form-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ms-phone">Phone</Label>
                  <Input
                    id="ms-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+61 400 000 000"
                    data-testid="member-form-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ms-amount">Sponsorship amount (AUD) *</Label>
                <Select value={amountChoice} onValueChange={setAmountChoice}>
                  <SelectTrigger className="h-11" data-testid="member-form-amount-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preset" data-testid="amount-option-preset">
                      AUD ${presetAmount.toLocaleString('en-AU', { minimumFractionDigits: 2 })} (recommended)
                    </SelectItem>
                    <SelectItem value="other" data-testid="amount-option-other">
                      Other — enter your own amount
                    </SelectItem>
                  </SelectContent>
                </Select>
                {amountChoice === 'other' && (
                  <div className="relative pt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="ms-amount"
                      type="number"
                      step="0.01"
                      min="1"
                      required={amountChoice === 'other'}
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="Enter amount"
                      className="pl-7"
                      data-testid="member-form-amount"
                      autoFocus
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Payment status</CardTitle>
              <CardDescription>Let us know if you've already paid or intend to pay shortly.</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={form.payment_status}
                onValueChange={(v) => setForm({ ...form, payment_status: v })}
                className="space-y-2"
              >
                <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/40">
                  <RadioGroupItem value="paid" id="ms-paid" className="mt-0.5" data-testid="member-form-status-paid" />
                  <div>
                    <p className="font-medium text-sm">I have already paid</p>
                    <p className="text-xs text-muted-foreground">An admin will verify the payment and email your tax invoice.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/40">
                  <RadioGroupItem value="yet_to_pay" id="ms-yet" className="mt-0.5" data-testid="member-form-status-yet" />
                  <div>
                    <p className="font-medium text-sm">I intend to pay shortly</p>
                    <p className="text-xs text-muted-foreground">We'll send you an invoice with payment instructions.</p>
                  </div>
                </label>
              </RadioGroup>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Notes (optional)</CardTitle>
              <CardDescription>Anything else we should know?</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Payment date, reference used, etc."
                data-testid="member-form-notes"
              />
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 text-base"
            data-testid="member-form-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
            Submit contribution
          </Button>
        </form>
      </main>
    </div>
  );
}
